import React, { useCallback, useEffect, useState } from 'react';
import * as yup from 'yup';
import { SxStyle } from '../../../../../types';
import { useStringsContext } from '../../../../../contexts/strings';
import AccordionWrapper from '../accordion';
import svStrings from '../../../../../sv-strings';
import TextInputWithValidate, {
  InputWithValidateProps,
} from '../../../../common/input/text-input-with-validate';
import Stack from '@mui/material/Stack';
import SelectWithValidate, { SelectOptions } from '../../../../common/input/select-with-validate';
import { Option } from '../../../../../types';
import RadioWithValidate from '../../../../common/input/radio-with-validate';
import {
  MatchFormData,
  PollFormData,
  usePushNotificationFormData,
} from '../../../../../hooks/pushNotification';
import { PushNotification, PushNotificationType } from '../../../../../types/notification';
import moment from 'moment';
import { useFormValidate } from '../../../../../hooks/formValidate';
import { PushNotificationValues } from '../../push-notification-send';

import { useLocation } from 'react-router';
import qs from 'query-string';

export type PushNotificationFormData = {
  formData?: MatchFormData | PollFormData;
  type: PushNotificationType;
};

const destinationKey = 'destination';
const webUrlKey: keyof PushNotification = 'webPageUrl';

const destinationAppContentKey: keyof PushNotification = 'type';
const destinationAppSceneKey: keyof PushNotification = 'scene';

const destinationOptions = ['url', 'app'] as const;
type DestinationOption = (typeof destinationOptions)[number];
const destinationOptionsText = [
  svStrings.pushFields.destinationOptionWebUrl,
  svStrings.pushFields.destinationOptionApp,
];

const appContentOptions: PushNotificationType[] = ['SCENE', 'MATCH', 'POLL'];
const appContentOptionsText = [
  svStrings.pushFields.destinationAppOptionScene,
  svStrings.pushFields.destinationAppOptionMatch,
  svStrings.pushFields.destinationAppOptionPoll,
];
const appContentValidation = yup.string().oneOf([...appContentOptions]);

type PushNotificationDataTypeKeys = keyof Pick<
  PushNotification,
  'matchId' | 'pollId' | 'webPageUrl' | 'leagueName' | 'season' | 'scene'
>;

const webUrlValidation = yup
  .string()
  .url(svStrings.validations.validUrl)
  .required(svStrings.validations.validUrl);

const styles: SxStyle = {
  container: {
    paddingLeft: '15px',
    paddingRight: '15px',
    paddingBottom: '24px',
  },
};

interface Props extends InputWithValidateProps<PushNotification> {
  expanded: boolean;
  selectedAbbrv: string;
  pushNotification: PushNotificationValues;
}

const PushNotificationDestination = ({
  expanded,
  selectedAbbrv,
  pushNotification,
  onValue,
  onValidation,
  onRemoveValue: onRemoveSendValue,
  hideValidationError,
}: Props) => {
  const { strings } = useStringsContext();

  const location = useLocation();
  const parsed = qs.parse(location.search);
  const matchId = parsed.match as string;

  const [destinationValue, setDestinationValue] = useState<DestinationOption>(
    matchId ? 'app' : 'url'
  );
  const [pushNotificationType, setPushNotificationType] =
    useState<PushNotificationType>('WEB_PAGE');
  const [pushNotificationFormData, setPushNotificationFormData] = useState<SelectOptions | null>(
    null
  );
  const [selectedDataKey, setSelectedDataKey] = useState<PushNotificationDataTypeKeys>('matchId');
  const [selectedDataValue, setSelectedDataValue] = useState<Option>();

  const { query } = usePushNotificationFormData(selectedAbbrv);
  const formData = query?.data;

  const { addFormValidation, isValid, removeFormValue } = useFormValidate();

  useEffect(() => {
    if (formData && pushNotificationType) {
      if (pushNotificationType === 'MATCH') {
        const matchFormOptions = formData?.matchFormData.map((item) => String(item.matchId));
        const matchFormOptionsText = formData?.matchFormData.map(
          (item) => `${moment(item.matchDate).format('YYYY-MM-DD HH:mm')} - ${item.matchName}`
        );
        setPushNotificationFormData({
          options: matchFormOptions,
          optionsText: matchFormOptionsText,
        });
        if (matchId) {
          setSelectedDataValue(matchId as Option);
        }
      } else if (pushNotificationType === 'POLL') {
        const pollFormOptions = formData?.pollFormData.map((item) => item.pollId);
        const pollFormOptionsText = formData?.pollFormData.map((item) => item.pollName);
        setPushNotificationFormData({
          options: pollFormOptions,
          optionsText: pollFormOptionsText,
        });
      } else if (pushNotificationType === 'SCENE') {
        // Set default and only scene value
        onValue?.(destinationAppSceneKey, 'start');
      }
    }
  }, [formData, matchId, onValue, pushNotificationType]);

  const onRemoveValues = useCallback(
    (keys: PushNotificationDataTypeKeys[]) => {
      keys.forEach((key) => {
        removeFormValue(key);
        onRemoveSendValue?.(key);
      });
    },
    [onRemoveSendValue, removeFormValue]
  );

  const onDestinationValue = useCallback(
    (key: string, value: DestinationOption) => {
      setDestinationValue(value);
      if (value === 'url') {
        onRemoveValues(['matchId', 'pollId', 'leagueName', 'season', 'scene']);
        onValue?.(destinationAppContentKey, 'WEB_PAGE');
      } else {
        onRemoveValues(['webPageUrl']);
        onValue?.(destinationAppContentKey, pushNotificationType);
      }
    },
    [onRemoveValues, onValue, pushNotificationType]
  );

  const onAppContentValue = useCallback(
    (key: keyof PushNotification, value: PushNotificationType) => {
      setPushNotificationType(value);
      if (value === 'MATCH' || value === 'POLL') {
        setSelectedDataKey(value === 'MATCH' ? 'matchId' : 'pollId');
      }
      onRemoveValues(['matchId', 'pollId', 'leagueName', 'season', 'scene']);
      if (value === 'SCENE') {
        onValue?.(destinationAppSceneKey, 'start');
      }
      onValue?.(key, value);
    },
    [onRemoveValues, onValue]
  );

  const onPushNotificationFormDataValue = useCallback(
    (key: keyof PushNotification, value: any) => {
      setSelectedDataValue(value);
      onValue?.(key, value);
      if (key === 'matchId' && formData) {
        const match = formData.matchFormData.find((item) => item.matchId === Number(value));
        if (match) {
          onValue?.('leagueName', match.leagueName);
          onValue?.('season', match.season);
        }
      }
    },
    [formData, onValue]
  );

  const onValidationChange = useCallback(
    (key: string, isValidationValid: boolean) => {
      addFormValidation(key, isValidationValid);
      onValidation?.(destinationKey, isValid);
    },
    [addFormValidation, isValid, onValidation]
  );

  return (
    <AccordionWrapper
      expanded={expanded}
      name={strings.pushFields.titleDestination}
      key={destinationKey}
    >
      <Stack spacing={3} sx={styles.container}>
        <RadioWithValidate
          name={destinationKey}
          options={[...destinationOptions]}
          optionsText={destinationOptionsText}
          label={strings.pushFields.destinationText}
          value={destinationValue}
          onValue={(key, value) => onDestinationValue(key, value as DestinationOption)}
        />
        {destinationValue === 'url' ? (
          <TextInputWithValidate<PushNotification>
            name={webUrlKey}
            label={strings.pushFields.destinationWebUrlLabel}
            value={pushNotification.webPageUrl}
            validation={webUrlValidation}
            onValue={onValue}
            helperText={strings.pushFields.destinationWebUrlHelperText}
            onValidation={onValidationChange}
            hideValidationError={hideValidationError}
          />
        ) : (
          <>
            <SelectWithValidate<PushNotification>
              name={destinationAppContentKey}
              options={[...appContentOptions]}
              optionsText={appContentOptionsText}
              label={strings.pushFields.destinationAppLabel}
              value={pushNotificationType}
              validation={appContentValidation}
              onValue={(key, value) => onAppContentValue(key, value as PushNotificationType)}
              onValidation={onValidationChange}
              hideValidationError={hideValidationError}
              fullWidth
            />
            {pushNotificationType !== 'SCENE' && (
              <SelectWithValidate<PushNotification>
                name={selectedDataKey}
                options={pushNotificationFormData?.options || []}
                optionsText={pushNotificationFormData?.optionsText || []}
                label={
                  pushNotificationType === 'MATCH'
                    ? svStrings.pushFields.destinationMatchLabel
                    : svStrings.pushFields.destinationPollLabel
                }
                value={selectedDataValue}
                onValue={onPushNotificationFormDataValue}
                disabled={!pushNotificationFormData?.options}
                validation={yup
                  .string()
                  .oneOf(
                    (pushNotificationFormData?.options as string[]) || [],
                    svStrings.validations.mustSelectAlternative
                  )
                  .required(svStrings.validations.isRequired)}
                onValidation={onValidationChange}
                hideValidationError={hideValidationError}
                fullWidth
              />
            )}
          </>
        )}
      </Stack>
    </AccordionWrapper>
  );
};

export default PushNotificationDestination;
